import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  AlertTitle,
} from "@mui/material";
import {
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";
import { localUrlEnum } from "../../Utils/UrlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";
import { examCategories } from "../../Utils/Constants";
import styles from "../../Styles/chooseExamSimulation.module.css";
import { isFreeSubscription } from "../../Utils/Utils";

interface Category {
  name: string;
  _id: string;
  label?: string;
  hasQuestions?: boolean;
}

export default function ChooseExamSimulation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { categories } = location.state || {};

  const handleStartExam = (category: Category) => {
    // if _id is defined than it is a product, if not it is a category
    const productId = category._id ? category._id : undefined;
    navigate(
      localUrlEnum.examQuestions +
        `?${
          productId ? `productId=${productId}` : `examCategory=${category.name}`
        }`
    );
  };

  const getCategoryData = (categoryName: string) => {
    const categoryKey = categoryName.toUpperCase();
    const categoryData = examCategories[categoryKey as keyof typeof examCategories] as any;
    return categoryData || {...examCategories.C, label:"" };
  };

  if (!categories || categories.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.contentWrapper}>
          <Box className={styles.emptyState}>
            <Typography className={styles.emptyStateText}>
              {Vocabulary.noDatasetsAvailable}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.contentWrapper}>
        {/* Header Section */}
        <Box className={styles.header}>
          <Typography variant="h3" className={styles.mainTitle}>
            {Vocabulary.chooseExamSimulation}
          </Typography>
        </Box>

        {/* Notice Section */}
        {!isFreeSubscription() && (
          <Box className={styles.noticeContainer}>
            <Alert severity="info" className={styles.noticeBox}>
              <Box className={styles.noticeContent}>
                <Box className={styles.noticeTextContainer}>
                  <AlertTitle className={styles.noticeTitle}>
                    {Vocabulary.multipleCategoriesNotice}
                  </AlertTitle>
                </Box>
              </Box>
            </Alert>
          </Box>
        )}

        {/* Categories Grid */}
        <Box className={styles.categoriesGrid}>
          {categories.map((category: Category) => {
            if(!category.hasQuestions) return null; // Skip categories without questions
            const categoryData = getCategoryData(category.name);
            return (
              <Card key={category._id} className={styles.categoryCard}>
                <CardContent className={styles.categoryCardContent}>
                  {/* Category Icon */}
                  <Box
                    className={styles.categoryIcon}
                    sx={{
                      bgcolor: "primary.main",
                      boxShadow: (theme) =>
                        `0 8px 24px ${theme.palette.primary.main}40`,
                    }}
                  >
                    <Box sx={{ color: "white", fontSize: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {categoryData?.icon}
                    </Box>
                  </Box>

                  {/* Category Label */}
                  <Typography variant="h4" className={styles.categoryLabel}>
                    {Vocabulary.category}: {category.name}
                  </Typography>

                  <Typography
                    variant="body2"
                    className={styles.categoryDescription}
                  >
                    {categoryData?.label}
                  </Typography>

                  {/* Start Button */}
                  <Button
                    variant="contained"
                    color="primary"
                    className={styles.startButton}
                    startIcon={<PlayArrowIcon />}
                    onClick={() => handleStartExam(category)}
                  >
                    {Vocabulary.startExamSimulation}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
